import { useEffect, useRef } from "react"
import { useLocation } from "@tanstack/react-router"

// GA4 measurement ID for the chictravel.ai web stream. Public, non-secret
// (it ships in the page either way), so it's hard-coded rather than configured.
const GA_MEASUREMENT_ID = `G-K63J8S2SHX`

// Hotjar site id for the chictravel.ai property (heatmaps + session recordings).
// Like the GA id it's a public, page-embedded value, so it's hard-coded too.
const HOTJAR_ID = 6726359
const HOTJAR_SV = 6

// GA only loads in the deployed production build. Both local `vite dev` and the
// e2e suite (which also runs `vite dev`, see playwright.config DEV_COMMAND) are
// `import.meta.env.PROD === false`, so our own dev sessions and every Playwright
// run are kept out of the prod property without any GA-console IP filtering.
const GA_ENABLED = import.meta.env.PROD

// sessionStorage marker so a Google OAuth signup — which navigates away to the
// provider and returns via a full page reload — can still emit `sign_up` once
// we're back and authenticated. The flush runs on the next Analytics init.
const PENDING_SIGNUP_KEY = `chic_pending_signup`

// gtag pushes its argument list (an `arguments` object) onto the dataLayer,
// so the queue holds heterogeneous entries — `unknown[]` keeps us off `any`.
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    // Hotjar's queue function, mirroring gtag's heterogeneous-args shape. `q` is
    // the pre-load command buffer its loader script later drains.
    hj?: ((...args: unknown[]) => void) & { q?: unknown[] }
    _hjSettings?: { hjid: number; hjsv: number }
  }
}

// Load the heavy analytics SCRIPTS (gtag.js ~160 KiB transfer, Hotjar) only
// AFTER the page has fully loaded AND the browser goes idle or the user shows
// intent — never in the initial wave. requestIdleCallback alone fired at ~150 ms
// on a cold simulated-mobile load (TBT is ~0, so the main thread is idle almost
// immediately), so gtag.js was still stealing slow-4G bandwidth from the
// framework bundle + CSS during the FCP/LCP window. Gating on the `load` event
// first pushes both scripts past that critical window; a first-interaction
// listener covers users who engage before idle. Event QUEUEING stays synchronous
// (cheap dataLayer pushes happen in the effect below regardless), and gtag drains
// the queue once it loads, so no page_view/sign_up is lost by the deferral.
const INTENT_EVENTS = [`pointerdown`, `keydown`, `touchstart`, `scroll`] as const

function whenIdle(cb: () => void) {
  if (typeof window === `undefined`) return
  let fired = false
  const run = () => {
    if (fired) return
    fired = true
    for (const evt of INTENT_EVENTS) window.removeEventListener(evt, run)
    cb()
  }
  // Arm the loaders only after `load`, so they can never contend with the
  // critical render. If the page already loaded (warm SPA nav), arm now.
  const arm = () => {
    for (const evt of INTENT_EVENTS)
      window.addEventListener(evt, run, { once: true, passive: true })
    const ric = window.requestIdleCallback
    if (typeof ric === `function`) ric(run, { timeout: 6000 })
    else window.setTimeout(run, 3000)
  }
  if (document.readyState === `complete`) arm()
  else window.addEventListener(`load`, arm, { once: true })
}

// Load Hotjar's tracking snippet once, client-side. This is the official inline
// snippet rewritten to run from our React init (the app is SSR-disabled) instead
// of a literal <head> <script> tag. Gated and path-suppressed by the same rules
// as GA so dev/e2e and owner/e2e-guide traffic never reach the recorder.
function initHotjar() {
  if (window.hj) return
  window.hj =
    window.hj ||
    function hj(...args: unknown[]) {
      ;(window.hj!.q = window.hj!.q || []).push(args)
    }
  window._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_SV }

  const script = document.createElement(`script`)
  script.async = true
  script.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_ID}.js?sv=${HOTJAR_SV}`
  document.head.appendChild(script)
}

// Paths that must never produce GA traffic. /admin is owner-only, and e2e seed
// guides — filtered from explore/sitemap by `%e2e%` on the name (guides.listPublic),
// which the slug inherits — are still publicly reachable by direct URL, so
// JS-running crawlers were creating sessions on them (the 2026-05-31 crawl wave).
function isSuppressedPath(pathname: string) {
  return (
    pathname.startsWith(`/admin`) ||
    (pathname.startsWith(`/guide/`) && pathname.includes(`e2e`))
  )
}

// Visiting /admin proves the browser belongs to the owner, so remember that and
// stamp every later hit from it `traffic_type=internal` — GA's Internal Traffic
// data filter then drops them. This follows the *device* across networks, unlike
// the console IP rules (which only cover the home connection and break silently
// when the ISP reassigns the address); the IP rules stay as a backstop for the
// first-ever visit, before the marker exists.
const INTERNAL_TRAFFIC_KEY = `chic_internal_traffic`

function isInternalBrowser() {
  try {
    return localStorage.getItem(INTERNAL_TRAFFIC_KEY) === `1`
  } catch {
    return false
  }
}

// Fire a GA4 event. No-ops everywhere GA isn't loaded (dev, e2e, or before
// gtag has initialized), so callers can emit unconditionally.
export function trackEvent(name: string, params?: Record<string, unknown>) {
  if (!GA_ENABLED || typeof window === `undefined` || !window.gtag) return
  if (isSuppressedPath(window.location.pathname)) return
  window.gtag(`event`, name, params)
}

// Record that a signup is mid-flight before an OAuth redirect leaves the page;
// Analytics flushes it into a `sign_up` event when the user lands back.
export function markPendingSignup(method: string) {
  try {
    sessionStorage.setItem(PENDING_SIGNUP_KEY, method)
  } catch {
    // Private-mode / blocked storage: drop the marker rather than throw mid-signup.
  }
}

// We mount GA client-side only (the app is SSR-disabled) and drive page_view
// ourselves: GA4's automatic page_view fires once on load, but TanStack routes
// client-side, so we suppress it (send_page_view:false) and emit on navigation.
export function Analytics() {
  const { pathname, searchStr } = useLocation()
  const initialized = useRef(false)

  // Init lazily, on the first non-suppressed page — not on mount. `gtag('config')`
  // alone creates a session (session_start + enhanced-measurement events fire even
  // with our page_view suppressed), so a visit that lands on /admin or an e2e guide
  // and leaves must never reach the config call at all. That was the leak: skipping
  // only page_view still counted those visits as sessions.
  useEffect(() => {
    if (isSuppressedPath(pathname)) {
      // Only the owner reaches /admin — mark the browser as internal for good.
      if (pathname.startsWith(`/admin`)) {
        try {
          localStorage.setItem(INTERNAL_TRAFFIC_KEY, `1`)
        } catch {
          // Storage unavailable — the IP-rule backstop still applies.
        }
      }
      return
    }
    if (!GA_ENABLED) {
      return
    }

    if (!initialized.current) {
      initialized.current = true

      window.dataLayer = window.dataLayer ?? []
      window.gtag = function gtag() {
        // gtag.js requires the raw `arguments` object, not a spread copy.
        window.dataLayer?.push(arguments)
      }
      window.gtag(`js`, new Date())
      window.gtag(`config`, GA_MEASUREMENT_ID, {
        send_page_view: false,
        // Config-level params ride on every subsequent event from this tag.
        ...(isInternalBrowser() ? { traffic_type: `internal` } : {}),
      })

      // Defer the heavy script loads to browser idle so they don't compete with
      // FCP/LCP. The config + page_view calls above already queued into dataLayer
      // synchronously, so they're sent intact once gtag.js drains the queue.
      whenIdle(() => {
        const script = document.createElement(`script`)
        script.async = true
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
        document.head.appendChild(script)

        // Hotjar piggybacks on the same first-non-suppressed-page init so it shares
        // GA's session-leak protection (no /admin or e2e-guide recordings).
        initHotjar()
      })

      // Flush a signup that completed via an OAuth redirect (Google). gtag queues
      // into dataLayer synchronously, so this lands even before the script loads.
      try {
        const pending = sessionStorage.getItem(PENDING_SIGNUP_KEY)
        if (pending) {
          sessionStorage.removeItem(PENDING_SIGNUP_KEY)
          window.gtag(`event`, `sign_up`, { method: pending })
        }
      } catch {
        // Storage unavailable — nothing to flush.
      }
    }

    // Emit a page_view on every client-side navigation (including the first one,
    // right after init above).
    window.gtag?.(`event`, `page_view`, {
      page_path: `${pathname}${searchStr}`,
      page_location: window.location.href,
      page_title: document.title,
    })
  }, [pathname, searchStr])

  return null
}
